import { useEffect, useState } from 'react'
import type { AgencyDocument, AgencyPdfHighlights } from '@/agent-v2/agency-documents/fetch/agency-document'
import type { AgencyDocumentViewerState } from '@/agent-v2/agency-documents/hooks/useAgencyDocument'
import { AgencyOriginalFormat, getAgencyOriginalFormat } from '@/agent-v2/agency-documents/utils/agency-original-format'
import { decodeAgencyTextOriginal, plainTextToHtml } from '@/agent-v2/agency-documents/utils/agency-text-original'
import { convertMarkdownToHtml } from '@/content-viewer/util/markdown-to-html'
import { ApiError } from '@/util/errors/api-error'
import { getAgencyDocumentById } from '../fetch/get-agency-document-by-id'
import { getAgencyDocumentOriginalById } from '../fetch/get-agency-document-original-by-id'

// Highlights come from a citation's saved evidence; a document opened by ID has none.
const NO_HIGHLIGHTS: AgencyPdfHighlights = []

export function useAgencyDocumentById(documentId: string): AgencyDocumentViewerState {
  const [data, setData] = useState<AgencyDocument | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [originalError, setOriginalError] = useState(false)
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    const controller = new AbortController()
    setData(null)
    setFile(null)
    setHtml(null)
    setError(false)
    setNotFound(false)
    setOriginalError(false)
    async function load() {
      try {
        const document = await getAgencyDocumentById(documentId, controller.signal)
        if (controller.signal.aborted) return
        setData(document)
        // Only a viewable stored original is fetched; every other format shows the unavailable state.
        const format = getAgencyOriginalFormat(document.media_type)
        if (!format || !document.original_available) return
        try {
          const original = await getAgencyDocumentOriginalById(documentId, controller.signal)
          if (controller.signal.aborted) return
          if (format === AgencyOriginalFormat.PDF) {
            setFile(new File([original.blob], original.fileName || 'decision.pdf', { type: 'application/pdf' }))
            return
          }
          const text = decodeAgencyTextOriginal(await original.blob.arrayBuffer())
          const nextHtml = format === AgencyOriginalFormat.MARKDOWN ? await convertMarkdownToHtml(text) : plainTextToHtml(text)
          if (controller.signal.aborted) return
          setHtml(nextHtml)
        } catch {
          if (!controller.signal.aborted) setOriginalError(true)
        }
      } catch (caught) {
        if (controller.signal.aborted) return
        if (caught instanceof ApiError && caught.status === 404) setNotFound(true)
        else setError(true)
      }
    }
    void load()
    return () => controller.abort()
  }, [documentId, attempt])
  return { data, file, pdfHighlights: NO_HIGHLIGHTS, html, error, notFound, originalError, retry: () => setAttempt((value) => value + 1) }
}
